/**
 * Marker w odpowiedzi Gemmy dla **klikalnych list wyboru** (pigułek).
 *
 * Składnia (dokładnie ta sama, którą opisuje prompt `backend/prompts/gemma-mysql-agent.md`):
 *
 * ```
 * [[unitresco:options]]
 * [
 *   {"label":"tekst widoczny", "send":"tekst, który pójdzie jako user message po kliknięciu"},
 *   {"label":"...", "send":"..."}
 * ]
 * [[/unitresco:options]]
 * ```
 *
 * UI:
 *  - usuwa cały blok z wyświetlanego tekstu bąbelka,
 *  - pod bąbelkiem renderuje „pigułki" (jedna grupa per blok),
 *  - klik w pigułkę → wywołanie `onPick(send)` → zwykle natychmiastowa wysyłka jako user message.
 */

export const GEMMA_INLINE_OPTIONS_OPEN = '[[unitresco:options]]';
export const GEMMA_INLINE_OPTIONS_CLOSE = '[[/unitresco:options]]';

export interface GemmaInlineOption {
  label: string;
  send: string;
}

export interface GemmaInlineOptionsGroup {
  options: GemmaInlineOption[];
}

export interface GemmaInlineOptionsParseResult {
  displayText: string;
  groups: GemmaInlineOptionsGroup[];
}

/**
 * Tolerancyjny regex: `[[...unitresco...options...]]` i `[[.../...unitresco...options...]]`
 *  - dopuszcza dowolne białe znaki wewnątrz nawiasów (`[[ unitresco : options ]]`),
 *  - dopuszcza dowolny bieg HTML-encoding wokół nawiasów (sanitizery często nic nie zmieniają),
 *  - rozpoznaje również lekkie literówki typu `[[/ unitresco:options ]]`.
 *
 * Otwierający / zamykający ROZRÓŻNIA po obecności `/`. Parser jest leniwy (`*?`),
 * więc wymyślenie otwierającego po zamykającym nie spowoduje chciwego dopasowania.
 */
const BLOCK_RE = /\[\[\s*unitresco\s*:\s*options\s*\]\]([\s\S]*?)\[\[\s*\/\s*unitresco\s*:\s*options\s*\]\]/gi;
const LOOSE_OPEN_RE = /\[\[\s*unitresco\s*:\s*options\s*\]\]/i;

function sanitizeString(v: unknown, maxLen = 500): string | null {
  if (typeof v !== 'string') return null;
  const trimmed = v.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, maxLen);
}

function stripOptionalMarkdownFences(raw: string): string {
  let s = raw.trim();
  // ```json ... ``` lub ``` ... ```
  const fence = s.match(/^```(?:json|js)?\s*([\s\S]*?)\s*```$/i);
  if (fence && fence[1]) s = fence[1].trim();
  // Otaczające `**` / `_` / backticki pojedyncze
  s = s.replace(/^(`+|\*+|_+)\s*/, '').replace(/\s*(`+|\*+|_+)$/, '').trim();
  return s;
}

function parseJsonLoose(raw: string): unknown {
  let trimmed = stripOptionalMarkdownFences(raw);
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    // Odetnij ewentualne ```json``` w środku stringa (gdyby model zapakował go w fence)
    const fenced = trimmed.match(/```(?:json|js)?\s*([\s\S]*?)```/);
    if (fenced && fenced[1]) {
      try {
        return JSON.parse(fenced[1].trim());
      } catch {
        /* ignore */
      }
    }
    // Spróbuj wyciąć pierwszą tablicę / obiekt i stopniowo skracać — naprawa drobnych syfów
    const firstArr = trimmed.indexOf('[');
    const firstObj = trimmed.indexOf('{');
    const starts = [firstArr, firstObj].filter(i => i >= 0);
    if (starts.length) {
      const start = Math.min(...starts);
      for (let end = trimmed.length; end > start; end--) {
        const slice = trimmed.slice(start, end);
        try {
          return JSON.parse(slice);
        } catch {
          /* try shorter */
        }
      }
    }
    return null;
  }
}

function coerceToOptions(parsed: unknown): GemmaInlineOption[] {
  const candidates: unknown[] = Array.isArray(parsed)
    ? parsed
    : parsed && typeof parsed === 'object' && Array.isArray((parsed as Record<string, unknown>).options)
      ? ((parsed as Record<string, unknown>).options as unknown[])
      : [];

  const out: GemmaInlineOption[] = [];
  for (const raw of candidates) {
    if (!raw || typeof raw !== 'object') continue;
    const obj = raw as Record<string, unknown>;
    const label = sanitizeString(obj.label) || sanitizeString(obj.text) || sanitizeString(obj.title);
    const send =
      sanitizeString(obj.send) ||
      sanitizeString(obj.value) ||
      sanitizeString(obj.reply) ||
      label; // fallback: jeśli brak `send`, wyślij `label`
    if (!label || !send) continue;
    out.push({ label, send });
    if (out.length >= 30) break; // twardy limit bezpieczeństwa
  }
  return out;
}

/**
 * Wyciąga wszystkie bloki opcji z tekstu i zwraca tekst do wyświetlenia (bez markerów)
 * oraz listę grup pigułek do wyrenderowania pod bąbelkiem (w kolejności wystąpień).
 *
 * Toleruje:
 *  - białe znaki w tagach (`[[ unitresco : options ]]`),
 *  - brak tagu zamykającego (wtedy zjadamy od otwierającego do końca tekstu, o ile dalej jest sensowny JSON),
 *  - JSON otoczony ```json``` lub `**`,
 *  - drobne śmieci za JSON-em (stopniowo skracamy, aż wejdzie `JSON.parse`).
 */
export function parseGemmaInlineOptions(text: string): GemmaInlineOptionsParseResult {
  if (!text || !LOOSE_OPEN_RE.test(text)) {
    return { displayText: text, groups: [] };
  }

  const groups: GemmaInlineOptionsGroup[] = [];
  let displayText = text.replace(BLOCK_RE, (_match, inner: string) => {
    const parsed = parseJsonLoose(inner);
    const options = coerceToOptions(parsed);
    if (options.length) {
      groups.push({ options });
    }
    return '';
  });

  // Fallback: pozostał niezamknięty tag otwierający. Spróbuj wyciąć tablicę JSON po nim.
  if (LOOSE_OPEN_RE.test(displayText)) {
    const openMatch = displayText.match(LOOSE_OPEN_RE);
    if (openMatch && openMatch.index != null) {
      const start = openMatch.index;
      const after = displayText.slice(start + openMatch[0].length);
      const parsed = parseJsonLoose(after);
      const options = coerceToOptions(parsed);
      if (options.length) {
        groups.push({ options });
        displayText = displayText.slice(0, start).replace(/\s+$/, '');
      }
    }
  }

  const cleaned = displayText
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .trim();

  return { displayText: cleaned, groups };
}
